import { ReactNode } from 'react';
import styled from 'styled-components';

import { formatPercentage } from '../constants';

import { TopEntity } from './useSlideoutSharedData';

export function StatTile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <StatBox>
      <StatLabel>{label}</StatLabel>
      {children}
    </StatBox>
  );
}

// "Avg X Rate" tile — colored primary value with a "N of M" addendum.
export function RateTile({
  label,
  tone,
  loading,
  rate,
  numerator,
  denominator,
  addendumSuffix,
}: {
  label: string;
  tone: 'green' | 'red';
  loading: boolean;
  rate: number | null;
  numerator: number;
  denominator: number;
  addendumSuffix?: string;
}) {
  return (
    <StatTile label={label}>
      <ValueRow>
        <RateValue $tone={tone}>{loading ? '—' : formatPercentage(rate)}</RateValue>
        {!loading && rate != null && (
          <Addendum>
            {numerator} of {denominator}
            {addendumSuffix && ` ${addendumSuffix}`}
          </Addendum>
        )}
      </ValueRow>
    </StatTile>
  );
}

// "Top X" / "Fastest X" tile — entity name with a pre-formatted addendum.
export function TopByTile({
  label,
  loading,
  entity,
  addendum,
}: {
  label: string;
  loading: boolean;
  entity: { name: string } | null;
  addendum?: string;
}) {
  return (
    <StatTile label={label}>
      <ValueRow>
        <Value>{loading || !entity ? '—' : entity.name}</Value>
        {!loading && entity && addendum && <Addendum>{addendum}</Addendum>}
      </ValueRow>
    </StatTile>
  );
}

// Three-tile row used by every rate metric drill (Acceptance, Denial): one RateTile plus a
// top-by receiving partner and a top-by plan, both formatted as "X.X% <ratePhrase>".
export function RateMetricTiles({
  rateLabel,
  rateTone,
  rate,
  count,
  total,
  ratePhrase,
  loading,
  topByLoading,
  topReceivingLabel,
  topReceiving,
  topPlanLabel,
  topPlan,
}: {
  rateLabel: string;
  rateTone: 'green' | 'red';
  rate: number | null;
  count: number;
  total: number;
  ratePhrase: string;
  loading: boolean;
  topByLoading: boolean;
  topReceivingLabel: string;
  topReceiving: TopEntity;
  topPlanLabel: string;
  topPlan: TopEntity;
}) {
  return (
    <>
      <RateTile
        label={rateLabel}
        tone={rateTone}
        loading={loading}
        rate={rate}
        numerator={count}
        denominator={total}
        addendumSuffix='referrals'
      />
      <TopByTile
        label={topReceivingLabel}
        loading={topByLoading}
        entity={topReceiving}
        addendum={topReceiving ? `${formatPercentage(topReceiving.value)} ${ratePhrase}` : undefined}
      />
      <TopByTile
        label={topPlanLabel}
        loading={topByLoading}
        entity={topPlan}
        addendum={topPlan ? `${formatPercentage(topPlan.value)} ${ratePhrase}` : undefined}
      />
    </>
  );
}

export const TilesGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, 1fr);
  gap: 12px;
`;

export const StatBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  padding: 24px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const StatLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
  letter-spacing: 0.18px;
`;

export const Value = styled.span`
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  color: ${({ theme }) => theme.colors.black};
`;

export const RateValue = styled.span<{ $tone: 'green' | 'red' | 'default' }>`
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  color: ${({ theme, $tone }) => {
    if ($tone === 'green') return theme.colors.accentGreen;
    if ($tone === 'red') return theme.colors.accentRed;
    return theme.colors.black;
  }};
`;

export const Addendum = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.black50};
`;

// Row of a primary metric followed by a small grey addendum (e.g. "302 of 694 referrals").
export const ValueRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;
